import { useId } from 'react'

// Wave 37 — perceived-quality primitive. Skeletons replace spinners on
// surfaces where the layout is known and the data is the only unknown.
// They reduce perceived load time even when the actual time is identical
// because the operator's eye lands on a recognizable shape instead of a
// spinning dot.
//
// Three primitives that compose into the higher-level retrofit shapes:
//   - SkeletonText  — one or more horizontal bars (line(s) of text)
//   - SkeletonCard  — a header bar + 2-3 body lines (matches InfoCard)
//   - SkeletonRow   — a list-row layout with a small badge slot + 2 lines
//
// All animate with the existing studio.css `studio-skeleton-shimmer`
// keyframes, which respect `prefers-reduced-motion`.

const BASE_BLOCK_STYLE = {
  background:
    'linear-gradient(90deg, rgba(180, 168, 150, 0.06) 0%, rgba(180, 168, 150, 0.14) 50%, rgba(180, 168, 150, 0.06) 100%)',
  backgroundSize: '200% 100%',
  borderRadius: 4,
  animation: 'studio-skeleton-shimmer 1.6s linear infinite',
}

export function SkeletonText({
  width = '100%',
  height = 14,
  marginBottom = 8,
  ariaLabel,
  ...rest
}) {
  return (
    <div
      role={ariaLabel ? 'status' : undefined}
      aria-label={ariaLabel}
      data-testid="skeleton-text"
      style={{
        ...BASE_BLOCK_STYLE,
        width,
        height,
        marginBottom,
      }}
      {...rest}
    />
  )
}

export function SkeletonCard({
  lines = 3,
  withBadge = false,
  height,
  message,
}) {
  const titleId = useId()
  return (
    <div
      role="status"
      aria-labelledby={titleId}
      data-testid="skeleton-card"
      style={{
        background: 'rgba(180, 168, 150, 0.04)',
        border: '1px solid rgba(180, 168, 150, 0.08)',
        borderRadius: 8,
        padding: 16,
        minHeight: height,
        position: 'relative',
      }}
    >
      <span
        id={titleId}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {message || 'Loading content…'}
      </span>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <SkeletonText width="40%" height={16} marginBottom={0} />
        {withBadge && (
          <div
            style={{
              ...BASE_BLOCK_STYLE,
              width: 60,
              height: 20,
              borderRadius: 999,
            }}
          />
        )}
      </div>
      {Array.from({ length: lines }, (_, i) => (
        <SkeletonText
          key={i}
          width={i === lines - 1 ? '60%' : '100%'}
          marginBottom={6}
        />
      ))}
    </div>
  )
}

export function SkeletonRow({ withBadge = true, withTimestamp = true }) {
  return (
    <div
      role="status"
      aria-label="Loading row"
      data-testid="skeleton-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        borderBottom: '1px solid rgba(180, 168, 150, 0.06)',
      }}
    >
      {withBadge && (
        <div
          style={{
            ...BASE_BLOCK_STYLE,
            width: 56,
            height: 20,
            borderRadius: 999,
            flex: 'none',
          }}
        />
      )}
      <div style={{ flex: 1 }}>
        <SkeletonText width="80%" height={14} marginBottom={6} />
        <SkeletonText width="50%" height={11} marginBottom={0} />
      </div>
      {withTimestamp && (
        <div
          style={{ ...BASE_BLOCK_STYLE, width: 70, height: 12, flex: 'none' }}
        />
      )}
    </div>
  )
}

export function SkeletonGrid({
  count = 6,
  columns = 'repeat(auto-fit, minmax(220px, 1fr))',
  gap = 12,
  cardLines = 3,
  message = 'Loading…',
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: columns, gap }}>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonCard key={i} lines={cardLines} message={message} />
      ))}
    </div>
  )
}

export function SkeletonList({
  count = 6,
  withBadge = true,
  withTimestamp = true,
  message = 'Loading list…',
}) {
  return (
    <div role="status" aria-label={message} data-testid="skeleton-list">
      {Array.from({ length: count }, (_, i) => (
        <SkeletonRow
          key={i}
          withBadge={withBadge}
          withTimestamp={withTimestamp}
        />
      ))}
    </div>
  )
}
